import React, { useMemo, useState } from "react";
import { Modal, Input, Button, Checkbox, Tag, message } from "antd";
import { shareDischargeSummaryMultiApi } from "../../../../services/discharge.services";
import useIsMobile from "../../../../hooks/useIsMobile";
import styles from "./ShareModal.module.css";
import {
  EMAIL_REGEX,
  MOBILE_REGEX,
} from "../../../../helpers/validationPatterns";



const digitsOnly = (value) => String(value || "").replace(/\D/g, "");

const contactRow = (key, label, tag, contact) => {
  const waNumber = digitsOnly(contact?.mobile);
  const email = contact?.email || "";
  return {
    key,
    label,
    tag,
    on: Boolean(waNumber || email),
    waOn: Boolean(waNumber),
    waNumber,
    emailOn: Boolean(email),
    email,
  };
};

const buildReceivers = (patientData) => {
  const admission = patientData?.admission;
  const patient = admission?.patient;

  const patientName =
    [patient?.first_name, patient?.last_name].filter(Boolean).join(" ") ||
    "Patient";

  const receivers = [
    {
      key: "patient",
      label: patientName,
      tag: null,
      on: true,
      waOn: true,
      waNumber: digitsOnly(patient?.mobile),
      emailOn: true,
      email: patient?.email || "",
    },
  ];

  const consultant = admission?.consultant_doctor;
  const doctorName =
    consultant?.doctor_name ||
    admission?.doctor_name ||
    admission?.primary_consultant_names?.[0];
  if (doctorName) {
    receivers.push(
      contactRow("doctor", doctorName, "Consultant Doctor", consultant)
    );
  }

  (admission?.partners || []).forEach((partner, index) => {
    const name = partner?.name;
    if (!name) return;
    receivers.push(
      contactRow(`partner-${index}`, name, `Partner ${index + 1}`, partner)
    );
  });

  return receivers;
};

const ShareModal = ({
  openModal,
  setOpenModal,
  id,
  practitioner,
  patientData,
  title = "Share Discharge Summary",
  documentLabel = "Discharge summary",
  shareApi,
  onBeforeSend,
}) => {
  const isMobile = useIsMobile();
  const postShare =
    shareApi || ((payload) => shareDischargeSummaryMultiApi(id, payload));
  const initialReceivers = useMemo(
    () => buildReceivers(patientData),
    [patientData]
  );
  const [receivers, setReceivers] = useState(initialReceivers);
  const [error, setError] = useState("");
  const [sharing, setSharing] = useState(false);

  const patch = (index, changes) => {
    setError("");
    setReceivers((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...changes } : row))
    );
  };

  const handleCancel = () => {
    if (sharing) return;
    setOpenModal(false);
  };

  const handleSend = async () => {
    const active = receivers.filter((row) => row.on);
    if (!active.length) {
      setError("Select at least one receiver.");
      return;
    }

    const withoutChannel = active.find((row) => !row.waOn && !row.emailOn);
    if (withoutChannel) {
      setError(`Select WhatsApp or Email for ${withoutChannel.label}.`);
      return;
    }

    const invalid = active.find(
      (row) =>
        (row.waOn && !MOBILE_REGEX.test(digitsOnly(row.waNumber))) ||
        (row.emailOn && !EMAIL_REGEX.test(row.email))
    );
    if (invalid) {
      setError(`Check the number or email for ${invalid.label}.`);
      return;
    }

    const emailList = active
      .filter((row) => row.emailOn)
      .map((row) => ({ name: row.label, email: row.email }));
    const whatsappList = active
      .filter((row) => row.waOn)
      .map((row) => ({ name: row.label, whatsapp: digitsOnly(row.waNumber) }));

    setSharing(true);
    try {
      if (onBeforeSend) {
        const saved = await onBeforeSend();
        if (!saved) return;
      }

      const resp = await postShare({
        ...(practitioner ? { practitioner } : {}),
        ...(emailList.length ? { email: emailList } : {}),
        ...(whatsappList.length ? { whatsapp: whatsappList } : {}),
      });

      const results = resp?.data?.results || {};
      const failures = ["email", "whatsapp"].flatMap((channel) =>
        (results[channel] || [])
          .filter((entry) => entry?.status && entry.status !== "success")
          .map((entry) => `${entry.name || entry[channel]} (${channel})`)
      );
      const attempted = emailList.length + whatsappList.length;

      if (!failures.length) {
        message.success(`${documentLabel} sent.`);
        setOpenModal(false);
        return;
      }

      if (failures.length >= attempted) {
        setError("Failed to share. Please try again.");
        return;
      }

      message.warning(`Sent, but failed for ${failures.join(", ")}.`);
      setOpenModal(false);
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to share. Please try again."
      );
    } finally {
      setSharing(false);
    }
  };

  return (
    <Modal
      title={title}
      open={openModal}
      onCancel={handleCancel}
      footer={null}
      width={isMobile ? "min(92vw, 400px)" : 820}
      centered={isMobile}
      className={styles.shareModal}
      maskClosable={!sharing}
      destroyOnClose
    >
      <div className={styles.body}>
        <div className={styles.grid}>
          <div className={styles.columnHead}>Receiver</div>
          <div className={styles.columnHead}>WhatsApp</div>
          <div className={styles.columnHead}>Email</div>

          {receivers.map((row, index) => (
            <React.Fragment key={row.key}>
              <label className={styles.receiver}>
                <Checkbox
                  checked={row.on}
                  onChange={(e) => patch(index, { on: e.target.checked })}
                />
                <span className={styles.receiverName}>{row.label}</span>
                {row.tag && (
                  <Tag className={styles.receiverTag}>{row.tag}</Tag>
                )}
              </label>

              <div className={styles.channel}>
                <Checkbox
                  checked={row.waOn}
                  disabled={!row.on}
                  aria-label={`Share with ${row.label} on WhatsApp`}
                  onChange={(e) => patch(index, { waOn: e.target.checked })}
                />
                <Input
                  value={row.waNumber}
                  disabled={!row.on || !row.waOn}
                  placeholder="WhatsApp number"
                  aria-label={`WhatsApp number for ${row.label}`}
                  onChange={(e) => patch(index, { waNumber: e.target.value })}
                />
              </div>

              <div className={styles.channel}>
                <Checkbox
                  checked={row.emailOn}
                  disabled={!row.on}
                  aria-label={`Share with ${row.label} by email`}
                  onChange={(e) => patch(index, { emailOn: e.target.checked })}
                />
                <Input
                  value={row.email}
                  disabled={!row.on || !row.emailOn}
                  placeholder="Email address"
                  aria-label={`Email address for ${row.label}`}
                  onChange={(e) => patch(index, { email: e.target.value })}
                />
              </div>
            </React.Fragment>
          ))}
        </div>

        {error && <div className={styles.error}>{error}</div>}
      </div>

      <div className={styles.footer}>
        <Button onClick={handleCancel} disabled={sharing}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSend} loading={sharing}>
          {sharing ? "Sending…" : "Send"}
        </Button>
      </div>
    </Modal>
  );
};

export default ShareModal;
